// theme.js - Chainlink-inspired theme for Material-UI
import { createTheme } from '@mui/material/styles';
import { red, blue } from '@mui/material/colors';

// Define Chainlink-inspired colors
const colors = {
  primary: {
    main: '#375BD2',        // Chainlink blue
    light: '#5C7CFA',
    dark: '#2D4DB2',
    contrastText: '#FFFFFF',
  },
  secondary: {
    main: '#0A2540',        // Chainlink navy
    light: '#375BD2',
    dark: '#041326',
    contrastText: '#FFFFFF',
  },
  error: {
    main: red[500],
    light: red[300],
    dark: red[700],
  },
  info: {
    main: blue[500],
    light: blue[300],
    dark: blue[700],
  },
  success: {
    main: '#3CCB7F',      // Chainlink green
    light: '#6DD9A5',
    dark: '#2E995F',
    contrastText: '#FFFFFF',
  },
  warning: {
    main: '#FFB531',
    light: '#FFC457',
    dark: '#E5A12C',
    contrastText: '#FFFFFF',
  },
};

const theme = createTheme({
  palette: {
    primary: colors.primary,
    secondary: colors.secondary,
    error: colors.error,
    info: colors.info,
    success: colors.success,
    warning: colors.warning,
    background: {
      default: '#F8F9FA',
      paper: '#FFFFFF',
    },
    text: {
      primary: colors.secondary.main,
      secondary: '#6C757D',
    },
  },
  typography: {
    fontFamily: "'Carlito', 'Segoe UI', 'Roboto', 'Helvetica', 'Arial', sans-serif",
    h1: {
      fontWeight: 700,
      fontSize: '2.5rem',
    },
    h2: {
      fontWeight: 700,
      fontSize: '2rem',
    },
    h3: {
      fontWeight: 600,
      fontSize: '1.75rem',
    },
    h4: {
      fontWeight: 600,
      fontSize: '1.5rem',
    },
    h5: {
      fontWeight: 600,
      fontSize: '1.25rem',
    },
    h6: {
      fontWeight: 600,
      fontSize: '1rem',
    },
    button: {
      textTransform: 'none',
      fontWeight: 600,
    },
  },
  shape: {
    borderRadius: 8,
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          padding: '10px 20px',
          boxShadow: 'none',
          '&:hover': {
            boxShadow: '0px 4px 10px rgba(55, 91, 210, 0.2)',
          },
        },
        containedPrimary: {
          background: 'linear-gradient(45deg, #375BD2 0%, #5C7CFA 100%)',
          '&:hover': {
            background: 'linear-gradient(45deg, #2D4DB2 0%, #375BD2 100%)',
          },
        },
        containedSecondary: {
          background: 'linear-gradient(45deg, #0A2540 0%, #1A3550 100%)',
          '&:hover': {
            background: 'linear-gradient(45deg, #041326 0%, #0A2540 100%)',
          },
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          boxShadow: '0px 2px 10px rgba(0, 0, 0, 0.05)',
        },
        colorPrimary: {
          backgroundColor: colors.primary.main,
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.05)',
          borderRadius: 12,
          overflow: 'hidden',
        },
      },
    },
    MuiCardHeader: {
      styleOverrides: {
        root: {
          padding: 16,
          backgroundColor: '#F5F7FE',
        },
        title: {
          fontWeight: 600,
        },
      },
    },
    MuiCardContent: {
      styleOverrides: {
        root: {
          padding: 24,
          '&:last-child': {
            paddingBottom: 24,
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        rounded: {
          borderRadius: 12,
        },
        elevation1: {
          boxShadow: '0px 2px 10px rgba(0, 0, 0, 0.05)',
        },
        elevation2: {
          boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.08)',
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          fontWeight: 500,
        },
      },
    },
  },
});

export default theme;